<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-gray-300 rounded-lg p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700">
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-700 pb-3">
        <div class="flex items-center gap-2">
          <i class="fas fa-ghost text-indigo-300"></i>
          <h2 class="text-xl font-semibold text-indigo-300">ไอเทมวิญญาณที่สวมใส่</h2>
          <span class="chip chip-green">สวมใส่ {{ equippedCount }}/3</span>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-indigo-300 transition-colors duration-200">
          <i class="fas fa-times text-lg"></i>
        </button>
      </div>

      <!-- Content -->
      <div v-if="loading" class="flex items-center justify-center py-10">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
      </div>

      <div v-else-if="error" class="text-center text-red-300 py-6">
        <p>{{ error }}</p>
      </div>

      <div v-else class="overflow-y-auto max-h-96">
        <!-- Fixed 3 slots in exact order -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div
            v-for="sub in GHOST_ORDER"
            :key="'slot-' + sub"
            class="rounded-md p-3 border transition-all duration-200 relative group"
            :class="ghostBySubtype[sub] ? 'bg-gray-800 border-gray-700 hover:border-indigo-600 cursor-pointer' : 'bg-gray-800 border-dashed border-gray-700 opacity-80'"
            @click="ghostBySubtype[sub] ? selectItem(ghostBySubtype[sub]) : null"
            @dblclick="ghostBySubtype[sub] ? dblClickItem(ghostBySubtype[sub]) : null"
          >
            <!-- Equipped card -->
            <template v-if="ghostBySubtype[sub]">
              <div class="relative flex flex-col items-center">
                <img
                  :src="ghostBySubtype[sub]?.img || 'default-ghost.png'"
                  alt="Ghost"
                  class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                  :class="[
                    ghostBySubtype[sub]?.rarityColors?.borderColor || 'border-gray-600',
                    ghostBySubtype[sub]?.rarityColors?.bgColor || 'bg-gray-800'
                  ]"
                  @error="handleImageError"
                />
                <div class="absolute top-0 right-0 -mt-2 -mr-2">
                  <span class="px-1.5 py-0.5 rounded text-2xs border border-gray-700 bg-gray-900 bg-opacity-80"
                      >
                    <i class="fas fa-star text-yellow-400 mr-1 text-2xs "></i>{{ ghostBySubtype[sub]?.rarity || 'ทั่วไป' }}
                  </span>
                </div>
              </div>

              <div class="w-full">
                <p class="text-xs font-medium text-center truncate"
                   :class="ghostBySubtype[sub]?.rarityColors?.textColor || 'text-gray-200'"
                   :title="ghostBySubtype[sub]?.name">
                  {{ ghostBySubtype[sub]?.name || 'ไม่ระบุ' }}
                </p>

                <!-- Cooldown / Status -->
                <div class="mt-2 text-center">
                  <p v-if="sub === SEARCH_SUBTYPE" class="text-2xs"
                     :class="(1 - searchGhostUses) >= 1 ? 'text-green-300' : 'text-yellow-300'">
                    <i class="fas fa-search mr-1"></i>
                    สถานะ: {{ (1 - searchGhostUses) >= 1 ? 'พร้อมใช้งาน' : 'ไม่พร้อมใช้งาน' }}
                  </p>

                  <p v-else-if="isOnCooldown(ghostBySubtype[sub])" class="text-2xs text-yellow-300">
                    <i class="fas fa-hourglass-half mr-1"></i>
                    คูดาวน์: {{ formatCooldown(ghostBySubtype[sub]) }}
                  </p>

                  <p v-else class="text-2xs text-green-300">
                    <i class="fas fa-check-circle mr-1"></i> พร้อมใช้งาน
                  </p>
                </div>
              </div>

              <!-- Selected ring -->
              <div
                v-if="selectedItem && selectedItem.id === ghostBySubtype[sub].id"
                class="pointer-events-none absolute inset-0 rounded-md ring-2 ring-indigo-600"
              ></div>
            </template>

            <!-- Placeholder card -->
            <template v-else>
              <div class="h-16 w-full flex items-center justify-center rounded border border-dashed border-gray-700 mb-2">
                <i class="fas fa-ghost text-2xl text-indigo-200"></i>
              </div>
              <p class="text-xs text-center text-gray-300 truncate">{{ sub }}</p>
              <p class="text-2xs text-center text-gray-400">ยังไม่ได้อัญเชิญ</p>
            </template>
          </div>
        </div>
      </div>

      <!-- Eliminated Rooms Result (for search ghost) -->
      <div v-if="eliminatedRooms.length > 0" class="bg-gray-700 p-4 rounded-md mt-1 border border-gray-600">
        <h3 class="text-sm font-semibold text-indigo-300 mb-1">ผลการค้นหาจากวิญญาณค้นหาผู้เล่น</h3>
        <p class="text-sm text-gray-200">ห้องที่ผู้รอดชีวิตไม่อยู่: {{ eliminatedRooms.join(', ') }}</p>
      </div>

      <!-- Search Ghost Uses Info -->
      <div
        v-if="selectedItem && selectedItem.subtype === SEARCH_SUBTYPE"
        class="bg-gray-700 p-4 rounded-md mt-1 border border-gray-600"
      >
        <h3 class="text-sm font-semibold text-indigo-300 mb-1">ข้อมูลการใช้งานวิญญาณค้นหาผู้เล่น</h3>
        <p class="text-sm text-gray-200">จำนวนครั้งที่ใช้ได้: {{ searchGhostUses }}/1</p>
        <p v-if="searchGhostUses > 0" class="text-2xs text-yellow-300 mt-1">
          ลอบสังหารเป้าหมายปัจจุบันหรือค้นหาใหม่เพื่อรีเซ็ตการใช้งาน
        </p>
      </div>

      <!-- Action Buttons -->
      <div v-if="selectedItem" class="flex justify-center gap-3 flex-wrap border-t border-gray-700 pt-3">
        <button
          v-if="ALLOWABLE_SUBTYPES.includes(selectedItem.subtype)"
          @click="useMagic"
          class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700"
          :disabled="isUsingMagic || isOnCooldown(selectedItem) || (selectedItem.subtype === SEARCH_SUBTYPE && searchGhostUses > 0)"
        >
          <span v-if="!isUsingMagic"><i class="fas fa-magic mr-1 text-xs"></i> ใช้เวทย์มนตร์</span>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
        </button>

        <button
          v-if="isAdmin && ALLOWABLE_SUBTYPES.includes(selectedItem.subtype)"
          @click="resetCooldown"
          class="bg-green-600 hover:bg-green-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-green-700"
          :disabled="isResettingCooldown"
        >
          <span v-if="!isResettingCooldown"><i class="fas fa-sync mr-1 text-xs"></i> รีเซ็ตคูดาวน์</span>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
        </button>
      </div>


      <!-- Detail Modal -->
      <DetailItemModal
        v-if="showDetailModal && selectedItem"
        :itemId="selectedItem.id"
        :item="selectedItem"
        @close="showDetailModal = false"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import DetailItemModal from '@/components/DetailItemModal.vue';
import gameItemsData from '@/data/gameItems.json';

export default {
  name: 'GhostItemsModal',
  emits: ['close', 'use-magic'],
  components: { DetailItemModal },
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    // Fixed order of 3 subtypes
    const GHOST_ORDER = Object.freeze([
      'วิญญาณเฝ้าห้อง',
      'วิญญาณค้นหาผู้เล่น',
      'วิญญาณผู้พิทักษ์'
    ]);
    const SEARCH_SUBTYPE = 'วิญญาณค้นหาผู้เล่น';
    const ALLOWABLE_SUBTYPES = GHOST_ORDER;
    const COOLDOWN_SUBTYPES = ['วิญญาณเฝ้าห้อง', 'วิญญาณผู้พิทักษ์'];

    // State
    const loading = ref(true);
    const error = ref(null);
    const isAdmin = computed(() => !!playerStore.userData?.admin);

    const equippedGhosts = ref([]);
    const ghostBySubtype = ref({});
    const selectedItem = ref(null);

    const isUsingMagic = ref(false);
    const isResettingCooldown = ref(false);
    const showDetailModal = ref(false);

    const eliminatedRooms = ref([]);
    const searchGhostUses = ref(0);
    const ghostCooldowns = ref({});

    let unsubscribeCharacterPrivate = null;

    // Derived
    const equippedCount = computed(() => Object.values(ghostBySubtype.value).filter(Boolean).length);

    // Helpers
    const buildSubtypeMap = (list) => {
      const map = {};
      for (const sub of GHOST_ORDER) map[sub] = null; // init 3 ช่อง
      list.forEach((g) => {
        const sub = g?.subtype;
        if (sub && GHOST_ORDER.includes(sub) && !map[sub]) {
          map[sub] = g; // ห้ามซ้ำ
        }
      });
      return map;
    };

    const normalizeGhostItem = (instanceId, itemData, fallbackSubtype = 'วิญญาณ') => {
      const template = gameItemsData[itemData.templateId] || {};
      return {
        id: instanceId,
        instanceId,
        templateId: itemData.templateId,
        states: itemData.states || {},
        ...template,
        rarity: itemData.states?.rarity ?? template.rarity ?? 1,
        rarityColors: itemData.states?.rarityColors ?? template.rarityColors ?? { bgColor: 'bg-gray-700', textColor: 'text-gray-500', borderColor: 'border-gray-600' },
        subtype: template.subtype || itemData.states?.subtype || fallbackSubtype,
        cooldownUntil: itemData.states?.cooldownUntil || itemData.cooldownUntil || null,
      };
    };

    // Realtime equipped ghosts
    const fetchEquippedGhosts = async () => {
      if (!user.value) {
        error.value = 'ไม่พบผู้ใช้';
        loading.value = false;
        return;
      }
      loading.value = true;
      try {
        unsubscribeCharacterPrivate = projectFirestore.collection('characterPrivate')
          .doc(user.value.uid)
          .onSnapshot(async (snapshot) => {
            try {
              if (!snapshot.exists) {
                equippedGhosts.value = [];
                ghostBySubtype.value = buildSubtypeMap([]);
                searchGhostUses.value = 0;
                ghostCooldowns.value = {};
                loading.value = false;
                return;
              }
              const data = snapshot.data();
              searchGhostUses.value = data.searchGhostUses || 0;
              ghostCooldowns.value = data.ghostCooldowns || {};

              const ghostItems = data.ghostItems || {};
              const ghostItemIds = data.ghostItemIds || [];
              const ghostTypes = data.ghostTypes || [];
              if (Object.keys(ghostItems).length) {
                const fetched = Object.entries(ghostItems).map(([instanceId, itemData]) => normalizeGhostItem(instanceId, itemData));
                ghostBySubtype.value = buildSubtypeMap(fetched);
                equippedGhosts.value = fetched;
              } else if (ghostItemIds.length) {
                const ghostPromises = ghostItemIds.map(async (itemId, index) => {
                  if (!itemId) return null;
                  const doc = await projectFirestore.collection('items').doc(itemId).get();
                  if (!doc.exists) return null;
                  return { id: itemId, instanceId: itemId, ...doc.data(), subtype: ghostTypes[index] || doc.data().subtype || 'วิญญาณ' };
                });
                const fetched = (await Promise.all(ghostPromises)).filter(Boolean);
                ghostBySubtype.value = buildSubtypeMap(fetched);
                equippedGhosts.value = fetched;
              } else {
                equippedGhosts.value = [];
                ghostBySubtype.value = buildSubtypeMap([]);
              }
              loading.value = false;
            } catch (innerErr) {
              console.error(innerErr);
              error.value = 'เกิดข้อผิดพลาดในการโหลดไอเทมวิญญาณ';
              loading.value = false;
            }
          }, (err) => {
            console.error(err);
            error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลตัวละคร';
            loading.value = false;
          });
      } catch (err) {
        console.error('Error setting up snapshot:', err);
        error.value = 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
        loading.value = false;
      }
    };

    // UI handlers
    const selectItem = (item) => {
      if (!item) return;
      selectedItem.value = item;
      showDetailModal.value = false;
    };

    const dblClickItem = (item) => {
      selectItem(item);
      openDetailModal();
    };

    const handleImageError = (e) => { e.target.src = 'default-ghost.png'; };

    const isOnCooldown = (item) => {
      if (!item) return false;
      const subtype = item.subtype;
      if (subtype === SEARCH_SUBTYPE) return false;
      const now = new Date();
      if (COOLDOWN_SUBTYPES.includes(subtype)) {
        const cd = ghostCooldowns.value[subtype];
        if (!cd) return false;
        let cdDate;
        if (typeof cd.toDate === 'function') cdDate = cd.toDate();
        else if (cd instanceof Date) cdDate = cd;
        else return false;
        return cdDate.getTime() > now.getTime();
      } else {
        const cd = item.cooldownUntil;
        if (!cd) return false;
        let cdDate;
        if (typeof cd === 'string') cdDate = new Date(cd);
        else if (typeof cd.toDate === 'function') cdDate = cd.toDate();
        else return false;
        return !isNaN(cdDate.getTime()) && cdDate.getTime() > now.getTime();
      }
    };

    const formatCooldown = (item) => {
      const subtype = item.subtype;
      if (subtype === SEARCH_SUBTYPE) return 'พร้อมใช้งาน';
      let cooldownUntil;
      if (COOLDOWN_SUBTYPES.includes(subtype)) {
        cooldownUntil = ghostCooldowns.value[subtype];
      } else {
        cooldownUntil = item.cooldownUntil;
      }
      if (!cooldownUntil) return 'พร้อมใช้งาน';
      let cd;
      if (typeof cooldownUntil === 'string') cd = new Date(cooldownUntil);
      else if (cooldownUntil && typeof cooldownUntil.toDate === 'function') cd = cooldownUntil.toDate();
      else return 'พร้อมใช้งาน';
      if (isNaN(cd.getTime())) return 'พร้อมใช้งาน';
      const now = new Date();
      if (cd <= now) return 'พร้อมใช้งาน';
      const diff = cd - now;
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return `${h} ชั่วโมง ${m} นาที`;
    };

    // Actions
    const useMagic = async () => {
      if (!user.value || !selectedItem.value?.id) {
        error.value = 'ไม่สามารถใช้เวทย์มนตร์ได้: ไม่พบผู้ใช้หรือวิญญาณ';
        return;
      }
      isUsingMagic.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'ghostCommandAction', subAction: 'useMagic', instanceId: selectedItem.value.id });
        if (result?.data?.success) {
          const cooldownHours = selectedItem.value.cooldownHours || 24;
          const cooldownUntil = new Date(Date.now() + cooldownHours * 60 * 60 * 1000).toISOString();
          if (COOLDOWN_SUBTYPES.includes(selectedItem.value.subtype)) {
            // No need to set locally, snapshot will update ghostCooldowns
          } 

          if (selectedItem.value.subtype === SEARCH_SUBTYPE && Array.isArray(result.data.eliminatedRooms)) {
            eliminatedRooms.value = result.data.eliminatedRooms;
          }

          emit('use-magic', {
            itemId: selectedItem.value.id,
            cooldownUntil,
            eventId: result.data.eventId,
            subtype: selectedItem.value.subtype
          });
        } else {
          error.value = result?.data?.message || 'การใช้เวทย์มนตร์ล้มเหลว';
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการใช้เวทย์มนตร์';
      } finally {
        isUsingMagic.value = false;
      }
    };

    const resetCooldown = async () => {
      if (!user.value || !selectedItem.value?.id) {
        error.value = 'ไม่สามารถรีเซ็ตคูดาวน์ได้: ไม่พบผู้ใช้หรือวิญญาณ';
        return;
      }
      isResettingCooldown.value = true;
      try {
        if (COOLDOWN_SUBTYPES.includes(selectedItem.value.subtype)) {
          await projectFirestore.collection('characterPrivate').doc(user.value.uid).update({
            [`ghostCooldowns.${selectedItem.value.subtype}`]: null
          });
        } else if (selectedItem.value.subtype !== SEARCH_SUBTYPE) {
          await projectFirestore.collection('characterPrivate').doc(user.value.uid).update({
            [`ghostItems.${selectedItem.value.id}.states.cooldownUntil`]: null
          });
          selectedItem.value.cooldownUntil = null;
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการรีเซ็ตคูดาวน์';
      } finally {
        isResettingCooldown.value = false;
      }
    };

    const openDetailModal = () => { showDetailModal.value = true; };

    onMounted(() => {
      fetchEquippedGhosts();
    });

    onUnmounted(() => {
      if (unsubscribeCharacterPrivate) unsubscribeCharacterPrivate();
    });

    return {
      // consts
      GHOST_ORDER, SEARCH_SUBTYPE, ALLOWABLE_SUBTYPES,
      // state
      loading, error, isAdmin,
      equippedGhosts, ghostBySubtype, selectedItem,
      isUsingMagic, isResettingCooldown, showDetailModal,
      eliminatedRooms, searchGhostUses,
      // derived
      equippedCount,
      // methods
      selectItem, dblClickItem, handleImageError, isOnCooldown, formatCooldown, useMagic, resetCooldown, openDetailModal,
      // expose for template
      ALLOWABLE_SUBTYPES: ALLOWABLE_SUBTYPES
    };
  }
};
</script>

<style scoped>
.max-h-96 { max-height: 24rem; }
.overflow-y-auto { overflow-y: auto; }

/* Animations (keep only safe ones) */
.animate-spin { animation: spin 1s linear infinite; }
.animate-spin-pulse { animation: spin-pulse 1.2s ease-in-out infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
.animate-modalFade { animation: modalFade 0.25s ease-out; }
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }

/* Scrollbar */
.overflow-y-auto::-webkit-scrollbar { width: 5px; }
.overflow-y-auto::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb:hover { background: #6b7280; }

/* Buttons */
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }

/* Chips */
.chip {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 0.95rem;
  border: 1px solid #374151;
  background: #111827;
  color: #d1d5db;
}
.chip-green {
  background: rgba(6, 78, 59, 0.25);
  color: #bbf7d0;
  border-color: #065f46;
}

/* Tailwind 2.x extra size */
.text-2xs { font-size: 0.7rem; line-height: 0.95rem; }
</style>
